import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';

const { FiChevronDown, FiUser, FiUsers } = FiIcons;

function AccountSwitcher() {
  const { user } = useAuth();
  const { athleteAccounts, currentAthleteId, isGuardian, switchAccount } = useGuardian();
  const [isOpen, setIsOpen] = useState(false);

  if (!isGuardian || athleteAccounts.length === 0) {
    return null;
  }

  const currentAccount = athleteAccounts.find(
    (account) => account.athlete_id === currentAthleteId
  );

  const getAccountName = (account) => {
    if (account.athlete?.first_name && account.athlete?.last_name) {
      return `${account.athlete.first_name} ${account.athlete.last_name}`;
    }
    return account.athlete?.email || 'Athlete Account';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiUsers} className="w-4 h-4 text-primary" />
        </div>
        <div className="text-left">
          <p className="text-xs text-gray-500">Managing</p>
          <p className="text-sm font-semibold text-gray-800">
            {currentAccount ? getAccountName(currentAccount) : 'Select Account'}
          </p>
        </div>
        <SafeIcon
          icon={FiChevronDown}
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full mt-2 right-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            >
              <div className="p-2">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase">
                    Athlete Accounts
                  </p>
                </div>
                <div className="mt-2 space-y-1">
                  {athleteAccounts.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => {
                        switchAccount(account.athlete_id);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                        account.athlete_id === currentAthleteId
                          ? 'bg-blue-50 text-primary'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          account.athlete_id === currentAthleteId
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <SafeIcon icon={FiUser} className="w-4 h-4" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-semibold">
                          {getAccountName(account)}
                        </p>
                        {account.athlete?.sport && (
                          <p className="text-xs text-gray-500">
                            {account.athlete.sport}
                            {account.athlete.graduation_year &&
                              ` • Class of ${account.athlete.graduation_year}`}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AccountSwitcher;